/**
 * /setup container (Phase 44.3).
 *
 * Three-step flow: Profile → Catalogue → Activate. The layout renders
 * a progress rail + shared footer; child routes render the step-specific
 * form. Loads the tenant's setup data once on mount and wires the store
 * for the child components.
 *
 * Routing-guard behaviour lives in SetupRouteGuard (component). This
 * component assumes the guard has already decided the caller belongs
 * here.
 */

import { useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header.jsx";
import LoadingSkeleton from "../../components/LoadingSkeleton.jsx";
import ErrorBubble from "../../components/ErrorBubble.jsx";
import RegenerationPoller from "./components/RegenerationPoller.jsx";
import { useSetupStore } from "../../stores/setupStore.js";

const STEPS = [
  { to: "/setup/profile", label: "1. Profile", key: "profile" },
  { to: "/setup/catalogue", label: "2. Catalogue", key: "catalogue" },
  { to: "/setup/activate", label: "3. Activate", key: "activate" },
];

function AutosaveIndicator() {
  const state = useSetupStore((s) => s.autosaveState);
  if (state === "idle") return null;
  return (
    <div className={`setup-autosave setup-autosave--${state}`}>
      {state === "saving" && "Saving…"}
      {state === "saved" && "All changes saved"}
      {state === "error" && "Couldn't save — check the errors highlighted below."}
    </div>
  );
}

export default function TenantSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const status = useSetupStore((s) => s.status);
  const error = useSetupStore((s) => s.error);
  const tenantStatus = useSetupStore((s) => s.tenantStatus);
  const loadSetup = useSetupStore((s) => s.loadSetup);
  const reset = useSetupStore((s) => s.reset);

  useEffect(() => {
    loadSetup();
    return () => reset();
  }, [loadSetup, reset]);

  // If the tenant flips to `active` while the admin is inside /setup
  // (e.g. they activated), bounce to the app home.
  useEffect(() => {
    if (tenantStatus === "active" && !location.pathname.includes("/setup/activate")) {
      navigate("/", { replace: true });
    }
  }, [tenantStatus, location.pathname, navigate]);

  if (status === "loading" && !useSetupStore.getState().profile) {
    return (
      <>
        <Header />
        <main className="setup-page">
          <div className="container">
            <LoadingSkeleton />
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="setup-page">
        <div className="container">
          <div className="setup-header">
            <h1>Tenant setup</h1>
            <p className="setup-header__muted">
              Review the drafted profile and catalogue, then activate your
              workspace to start running pipelines.
            </p>
          </div>

          {error && <ErrorBubble message={error} />}

          <nav className="setup-steps" aria-label="Setup progress">
            {STEPS.map((step) => (
              <NavLink
                key={step.key}
                to={step.to}
                className={({ isActive }) =>
                  `setup-steps__step ${isActive ? "setup-steps__step--active" : ""}`
                }
              >
                {step.label}
              </NavLink>
            ))}
          </nav>

          <div className="setup-content">
            <Outlet />
          </div>

          <AutosaveIndicator />
        </div>
      </main>
      {/* Mounted at the page level so a regeneration-in-flight survives
          navigation between /setup/profile and /setup/catalogue. */}
      <RegenerationPoller />
    </>
  );
}
